import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import type { DatasetFile, DatasetItem } from '@/lib/contract';
import { normalizeDatasetDetail } from '@/lib/normalize';
import { canSubmit, useSubmitForEvaluation } from '@/lib/queries/dataset-submit';
import {
  collectionLabel,
  formatBytes,
  formatDay,
  formatMoment,
  formatHours,
  modalityLine,
  orDash,
  statusLabel,
  statusTone
} from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { Button } from '@/ui/button';
import { MicroLabel } from '@/ui/micro-label';
import { StatTile } from '@/ui/stat-tile';
import { StatusDot } from '@/ui/status-dot';
import { ErrorState } from '@/ui/error-state';
import { Badge } from '@/ui/badge';
import { GateBadge } from '@/domain/gate-badge';
import { QualityReportPanel } from '@/domain/quality-report';
import { useRealtimeRefresh } from '@/lib/realtime';

/**
 * What a vendor is told about their own submission.
 *
 * Everything here already existed on the API and nothing on the client called it: the verdict and
 * its five blocks, the flags raised against it, the stage history, and the generated report. A
 * vendor who can see only a gate letter cannot act on it -- the blocks and flags are the part that
 * says what to fix.
 *
 * It is the operator's `QualityReportPanel`, not a vendor rendition of it. One run, one verdict,
 * described the same way on both sides.
 */
export function VendorSubmissionDetail() {
  const { id = '' } = useParams();
  const [submitError, setSubmitError] = useState('');
  const submit = useSubmitForEvaluation({ onError: setSubmitError });

  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['vendor', 'dataset', id],
    queryFn: async () => normalizeDatasetDetail((await api(`/datasets/${id}`)) as Record<string, unknown>)
  });

  /**
   * The report is written by the ancillary lane a moment *after* the verdict lands, so a null here
   * is "not yet", not "never" -- it is a separate query that the same realtime hint re-runs, rather
   * than a field of the detail that would have to be fetched again whole.
   */
  const report = useQuery({
    queryKey: ['vendor', 'dataset', id, 'report'],
    queryFn: () => api<{ url: string } | null>(`/datasets/${id}/black-box-run/report-url`)
  });

  const history = useQuery({
    queryKey: ['vendor', 'dataset', id, 'history'],
    queryFn: () =>
      api<{ event_type: string; created_at: string; metadata: Record<string, unknown> | null }[]>(
        `/datasets/${id}/pipeline-history`
      )
  });

  useRealtimeRefresh(
    `/datasets/${id}/black-box-run/subscription-token`,
    [
      ['vendor', 'dataset', id],
      ['vendor', 'dataset', id, 'report'],
      ['vendor', 'dataset', id, 'history']
    ],
    { realm: 'vendor' }
  );

  if (isError)
    return <ErrorState error={error} onRetry={() => void refetch()} title="Could not load this submission" />;
  if (isPending || !data)
    return (
      <div className="grid place-items-center rounded border border-border bg-card px-6 py-20">
        <MicroLabel size="xs">Loading submission</MicroLabel>
      </div>
    );

  const specLine = [modalityLine(data.modalities), collectionLabel(data.collection_type), data.rig, data.format_label]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="grid gap-4">
      <nav className="flex items-center gap-2">
        <Link
          to="/v/submissions"
          className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase hover:text-muted"
        >
          Submissions
        </Link>
        <span className="text-dim">/</span>
        <span className="font-mono text-[10px] tracking-[0.12em] text-muted uppercase">
          {data.dataset_code ?? data.name}
        </span>
      </nav>

      <PageHeader
        title={data.name}
        subtitle={specLine || undefined}
        actions={
          <div className="flex items-center gap-2">
            {report.data?.url ? (
              <Button asChild variant="secondary">
                <a href={report.data.url} target="_blank" rel="noreferrer">
                  <Download className="size-3.5" />
                  QA report
                </a>
              </Button>
            ) : (
              // Said plainly rather than hidden: a missing report before the verdict is a stage, and
              // after a verdict that produced none it is a fact worth seeing.
              <MicroLabel size="xs">{data.gate ? 'Report generating' : 'No report yet'}</MicroLabel>
            )}
            {/*
              The same action as the table's, on the screen a vendor is most likely to be on after
              fixing something. It was only ever on the list, so resolving a blocked file here meant
              navigating back to submit -- and this screen knows more than the list does: it can name
              the file that is blocking before the attempt is made.
            */}
            {canSubmit(data.lifecycle_status) ? (
              <Button disabled={submit.isPending} onClick={() => submit.mutate(id)}>
                {submit.isPending ? 'Submitting…' : 'Submit for evaluation'}
              </Button>
            ) : null}
          </div>
        }
      />

      {submitError ? (
        <p className="rounded border border-danger/40 bg-card px-4 py-3 text-[13px] text-danger" role="alert">
          {submitError}
        </p>
      ) : null}

      <div className="grid grid-cols-4 gap-4 max-lg:grid-cols-2">
        <StatTile
          label="Stage"
          value={<StatusDot tone={statusTone(data.lifecycle_status)} label={statusLabel(data.lifecycle_status)} />}
          foot={data.submitted_at ? `Submitted ${formatDay(data.submitted_at)}` : 'Not submitted yet'}
        />
        <StatTile label="Gate" value={<GateBadge gate={data.gate} />} foot={orDash(data.version_label)} />
        <StatTile
          label="Quality score"
          value={data.quality_score ?? '—'}
          foot={data.open_flag_count ? `${data.open_flag_count} open flags` : 'no open flags'}
        />
        <StatTile label="Hours" value={formatHours(data.hours)} foot={`${data.episode_count ?? 0} episodes`} />
      </div>

      {/* The outcome, first: it is the thing a vendor opened this page to find, and the note is the
          only part of the operator's reasoning they ever see. */}
      {data.decision ? (
        <Panel
          title="Decision"
          action={
            <Badge tone={data.decision.decision === 'approved' ? 'pass' : 'danger'}>{data.decision.decision}</Badge>
          }
          subtitle={`Recorded ${formatDay(data.decision.decided_at)}`}
        >
          <p className="text-[13px] leading-relaxed text-foreground">
            {data.decision.note || <span className="text-dim">No note was recorded with this decision.</span>}
          </p>
        </Panel>
      ) : null}

      <VersionsPanel datasetId={id} current={data.package_version} />

      <FilesPanel datasetId={id} items={data.items} submitted={!!data.submitted_at} />

      {data.quality_report ? (
        <QualityReportPanel report={data.quality_report} flags={data.flags} />
      ) : (
        <Panel title="Quality report">
          <p className="text-[13px] leading-relaxed text-muted">
            No evaluation has produced a verdict for this batch yet. The stage above is where it currently stands.
          </p>
        </Panel>
      )}

      <Panel title="Pipeline history" subtitle="Every stage this batch has been through">
        {history.isPending ? (
          <MicroLabel size="xs">Loading…</MicroLabel>
        ) : history.isError ? (
          <ErrorState error={history.error} onRetry={() => void history.refetch()} title="Could not load the history" />
        ) : history.data?.length ? (
          <ul className="grid">
            {(history.data ?? []).map((event, index) => (
              <li
                key={`${event.created_at}-${index}`}
                className="grid grid-cols-[160px_1fr] items-center gap-3 border-t border-border py-2 first:border-t-0"
              >
                <span className="font-mono text-[11px] text-muted">{formatMoment(event.created_at)}</span>
                <span className="font-mono text-[11px] text-foreground">{event.event_type.replaceAll('_', ' ')}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="font-mono text-[11px] text-dim">Nothing recorded yet.</p>
        )}
      </Panel>
    </div>
  );
}

interface VersionRow {
  package_version: number;
  state: 'open' | 'frozen';
  opened_at: string;
  frozen_at: string | null;
  file_count: number;
  carried_count: number;
}

/**
 * Every attempt at this dataset, and which one is being edited.
 *
 * The operator sees the same history as a lineage of *verdicts* (`domain/version-lineage.tsx`,
 * one node per evaluated version). The vendor needs the other half: which version is still open,
 * what it holds, and how much of it was carried rather than re-uploaded — the answer to "did my
 * re-upload actually do anything".
 */
function VersionsPanel({ datasetId, current }: { datasetId: string; current: number }) {
  const versions = useQuery({
    queryKey: ['vendor', 'dataset', datasetId, 'versions'],
    queryFn: () => api<{ data: VersionRow[] }>(`/datasets/${datasetId}/versions`)
  });
  const rows = versions.data?.data ?? [];
  // One version and nothing carried is the ordinary first upload: a panel saying "v1" adds nothing.
  if (rows.length <= 1 && !rows[0]?.carried_count) return null;

  return (
    <Panel title="Versions" subtitle={`Currently editing v${current}`}>
      <ul className="grid">
        {rows.map((row, index) => (
          <li
            key={row.package_version}
            className={cn('flex items-center gap-3 py-2.5', index > 0 && 'border-t border-border')}
          >
            <span className="w-10 font-mono text-[13px] text-foreground">v{row.package_version}</span>
            <Badge tone={row.state === 'open' ? 'primary' : 'neutral'}>
              {row.state === 'open' ? 'editing' : 'submitted'}
            </Badge>
            <span className="flex-1 font-mono text-[11px] text-muted">
              {row.file_count} {row.file_count === 1 ? 'file' : 'files'}
              {/* The saving, stated. It is the difference between a remediation and a re-upload. */}
              {row.carried_count ? ` · ${row.carried_count} carried forward` : ''}
            </span>
            <MicroLabel size="xs">{formatDay(row.frozen_at ?? row.opened_at)}</MicroLabel>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

/**
 * A file the vendor has to do something about.
 *
 * Two independent ways to be one, because there are two verdicts. `rejected` / `check_error` is
 * *intake* — the bytes never became admissible. `qa_verdict === 'REJECTED'` is the evaluation's
 * answer about what is inside them, which arrives much later and which a `ready` file can perfectly
 * well receive. Treating only the first as "a problem" is what let four rejected MCAPs sit in a
 * list marked `ready` while a flag pointed at one of them.
 */
const isBlocking = (file: DatasetFile) =>
  file.status === 'rejected' || file.status === 'check_error' || file.qa_verdict === 'REJECTED';

/** The two verdicts on one row, each shown only once it has an answer. */
function FileRow({ file, action }: { file: DatasetFile; action?: React.ReactNode }) {
  return (
    <li className="grid gap-2 py-3">
      <div className="flex items-center gap-3">
        <span className="flex-1 truncate font-mono text-[12px] text-foreground" title={file.relative_path ?? undefined}>
          {file.relative_path || file.original_name}
        </span>
        <span className="font-mono text-[11px] text-muted">{formatBytes(file.file_size_bytes)}</span>
        {/* Intake. `ready` here means admissible, not approved -- the badge beside it says the rest. */}
        <Badge tone={file.status === 'ready' ? 'pass' : isBlocking(file) ? 'danger' : 'neutral'}>
          {file.status.replaceAll('_', ' ')}
        </Badge>
        {file.qa_verdict ? (
          <Badge tone={file.qa_verdict === 'REJECTED' ? 'danger' : 'pass'}>{file.qa_code || file.qa_verdict}</Badge>
        ) : null}
        {action}
      </div>
      {file.blocking_summary ? (
        <p className="text-[12px] leading-relaxed text-danger">{file.blocking_summary}</p>
      ) : file.qa_verdict === 'REJECTED' && file.qa_message ? (
        <p className="text-[12px] leading-relaxed text-danger">{file.qa_message}</p>
      ) : null}
    </li>
  );
}

/**
 * The files in a batch, problems first.
 *
 * This panel exists so a vendor can answer one question: *which file do I fix?* A LeRobot upload is
 * three hundred files and a remediation touches two of them, so listing everything by default buries
 * the answer in the noise it is supposed to cut through. What needs action is always visible; the
 * rest is one line and an expander, and the expanded list is height-capped rather than endless.
 */
function FilesPanel({ datasetId, items, submitted }: { datasetId: string; items: DatasetItem[]; submitted: boolean }) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState('');
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState(false);
  const files = items.flatMap(item => item.files);
  const blocking = files.filter(isBlocking);
  const rest = files.filter(file => !isBlocking(file));

  const remove = useMutation({
    mutationFn: (fileId: string) => api(`/datasets/${datasetId}/uploads/${fileId}`, { method: 'DELETE' }),
    onMutate: (fileId: string) => {
      setError('');
      setRemoving(fileId);
    },
    onSuccess: () => {
      // Removing the last blocker re-derives the stage server-side, so both the batch and the list
      // it came from have to be re-read rather than patched here.
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'dataset', datasetId] });
      void queryClient.invalidateQueries({ queryKey: ['vendor', 'datasets'] });
    },
    onError: cause => setError(cause instanceof ApiError ? cause.message : 'Could not remove the file.'),
    onSettled: () => setRemoving('')
  });

  if (!files.length)
    return (
      <Panel title="Files">
        <p className="text-[13px] leading-relaxed text-muted">Nothing has been uploaded into this batch yet.</p>
      </Panel>
    );

  return (
    <Panel
      title="Files"
      subtitle={
        blocking.length
          ? `${files.length} ${files.length === 1 ? 'file' : 'files'} · ${blocking.length} need${blocking.length === 1 ? 's' : ''} attention`
          : `${files.length} ${files.length === 1 ? 'file' : 'files'}`
      }
    >
      {error ? <p className="mb-3 text-[12px] text-danger">{error}</p> : null}

      <ul className="grid divide-y divide-border">
        {blocking.map(file => (
          <FileRow
            key={file.id}
            file={file}
            action={
              submitted ? null : (
                <Button
                  size="sm"
                  variant="danger"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(file.id)}
                  className="whitespace-nowrap"
                >
                  {removing === file.id ? 'Removing…' : 'Remove'}
                </Button>
              )
            }
          />
        ))}
      </ul>

      {rest.length ? (
        <div className={cn(blocking.length && 'mt-3 border-t border-border pt-3')}>
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="flex w-full items-center justify-between text-left text-[12px] text-muted hover:text-foreground"
          >
            <span>
              {rest.length} {rest.length === 1 ? 'file' : 'files'} passed
            </span>
            <MicroLabel size="xs">{expanded ? 'Hide' : 'Show all'}</MicroLabel>
          </button>
          {/*
            Capped and scrolled rather than allowed to run: three hundred rows below a two-row answer
            is the bloat this panel exists to avoid, and an expander that pushes the page to ten
            screens has only moved the problem behind a click.
          */}
          {expanded ? (
            <ul className="mt-2 grid max-h-80 divide-y divide-border overflow-y-auto">
              {rest.map(file => (
                <FileRow key={file.id} file={file} />
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {blocking.length && !submitted ? (
        <MicroLabel size="xs" className="mt-4 block leading-relaxed">
          A batch cannot be submitted while any file has failed its checks · remove it, or upload a corrected batch
        </MicroLabel>
      ) : null}
    </Panel>
  );
}
